import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RateLimitError } from '../types';
import { rateLimitService, isUnlimitedValue, isMonthlyResource } from '../services/rateLimitService';

interface RateLimitModalProps {
  error: RateLimitError;
  onClose: () => void;
}

// Friendly tier labels
const TIER_LABELS: Record<string, string> = {
  'PERSONAL_GUEST': 'Free Plan',
  'PERSONAL': 'Personal Plan',
  'BUSINESS': 'Business Plan',
  'BUSINESS_PLUS': 'Business Plus Plan',
};

const RateLimitModal: React.FC<RateLimitModalProps> = ({ error, onClose }) => {
  const navigate = useNavigate();
  const resourceLabel = rateLimitService.getResourceLabel(error.resourceType);
  const tierLabel = TIER_LABELS[error.tierType] || error.tierType;

  const handleManageSubscription = () => {
    onClose();
    navigate('/#shopAdmin/subscription');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '20px',
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: 'var(--warning-bg)',
            borderBottom: '1px solid var(--warning)',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'var(--warning-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
            }}
          >
            &#9888;
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--warning-text)' }}>
              Usage Limit Reached
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--error-text)' }}>
              {isMonthlyResource(error.resourceType)
                ? `${resourceLabel} limit for this month`
                : `${resourceLabel} limit reached`}
            </p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <p style={{ margin: '0 0 20px 0', fontSize: '15px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
            {error.rateLimitMessage ||
              (isMonthlyResource(error.resourceType)
                ? `You've reached your ${resourceLabel.toLowerCase()} limit for this billing period.`
                : `You've reached your ${resourceLabel.toLowerCase()} limit for your current plan.`)}
          </p>

          {/* Usage display */}
          <div
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Current Usage</span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                {error.currentUsage} / {isUnlimitedValue(error.maxAllowed) ? 'Unlimited' : error.maxAllowed}
              </span>
            </div>

            {/* Progress bar */}
            <div
              style={{
                height: '8px',
                backgroundColor: 'var(--bg-accent)',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '12px',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  backgroundColor: 'var(--warning)',
                  borderRadius: '4px',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>Current Plan</span>
              <span
                style={{
                  fontSize: '12px',
                  color: 'var(--warning-text)',
                  backgroundColor: 'var(--warning-bg)',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontWeight: 500,
                }}
              >
                {tierLabel}
              </span>
            </div>
          </div>

          {/* Info text */}
          <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
            Upgrade your plan to get higher limits and unlock more features for your shop.
          </p>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 20px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
              }}
            >
              Close
            </button>
            <button
              onClick={handleManageSubscription}
              style={{
                flex: 1,
                padding: '12px 20px',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--error)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--primary)';
              }}
            >
              <span>&#9733;</span>
              <span>Manage Subscription</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateLimitModal;
